import {
  CalendarRange,
  BookOpenText,
  ClipboardList,
  Command,
  Factory,
  HelpCircle,
  LogOut,
  Mail,
  Menu as MenuIcon,
  Search,
  Settings,
  ShieldCheck,
  ShieldAlert,
  FlaskConical,
  UserRound,
  Users,
  X
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import type { BootstrapData } from "../lib/types";
import { api } from "../lib/api";
import { localeNames, supportedLocales, useI18n } from "../lib/i18n";
import { Avatar, Button } from "./ui";

const routeMeta: Array<{ pattern: RegExp; sectionKey: string; titleKey: string; descriptionKey: string }> = [
  { pattern: /^\/schedule/, sectionKey: "nav.planning", titleKey: "nav.schedule", descriptionKey: "page.schedule.description" },
  { pattern: /^\/requests/, sectionKey: "nav.planning", titleKey: "nav.requests", descriptionKey: "page.requests.description" },
  { pattern: /^\/assets/, sectionKey: "nav.administration", titleKey: "nav.assets", descriptionKey: "page.assets.description" },
  { pattern: /^\/admin\/specifications/, sectionKey: "nav.administration", titleKey: "page.specifications.title", descriptionKey: "page.specifications.description" },
  { pattern: /^\/admin\/users/, sectionKey: "nav.administration", titleKey: "nav.users", descriptionKey: "page.users.description" },
  { pattern: /^\/admin\/audit/, sectionKey: "nav.administration", titleKey: "nav.audit", descriptionKey: "page.audit.description" },
  { pattern: /^\/admin\/security/, sectionKey: "nav.administration", titleKey: "nav.security", descriptionKey: "page.security.description" },
  { pattern: /^\/admin\/email-test/, sectionKey: "nav.administration", titleKey: "nav.emailTest", descriptionKey: "page.emailTest.description" },
  { pattern: /^\/profile/, sectionKey: "breadcrumb.account", titleKey: "menu.profile", descriptionKey: "page.profile.description" }
];

export function Shell({ data, children }: { data: BootstrapData; children: ReactNode }) {
  const { locale, setLocale, t } = useI18n();
  const [navOpen, setNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [commandSearch, setCommandSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const meta = routeMeta.find((item) => item.pattern.test(location.pathname)) || routeMeta[0];
  const roles = data.user.roles.split(",").map((role) => role.trim());
  const canAdmin = roles.some((role) => role === "admin" || role === "superadmin");
  const canSuperadmin = roles.includes("superadmin");
  const environment = data.environment || { mode: "live" as const };
  const isTest = environment.mode === "test";

  useEffect(() => {
    document.documentElement.dataset.environment = environment.mode;
    return () => { delete document.documentElement.dataset.environment; };
  }, [environment.mode]);

  const switchEnvironment = async () => {
    await api("/api/environment/switch", {
      method: "POST",
      body: JSON.stringify({ mode: isTest ? "live" : "test" })
    });
    window.location.reload();
  };

  const navigation = [
    { label: t("nav.schedule"), to: "/schedule", icon: CalendarRange },
    { label: t("nav.requests"), to: "/requests", icon: ClipboardList },
    ...(canAdmin ? [{ label: t("nav.assets"), to: "/assets", icon: Factory }] : []),
    ...(canAdmin ? [{ label: t("nav.specifications"), to: "/admin/specifications", icon: BookOpenText }] : []),
    ...(canSuperadmin ? [{ label: t("nav.users"), to: "/admin/users", icon: Users }] : []),
    ...(canSuperadmin ? [{ label: t("nav.audit"), to: "/admin/audit", icon: ShieldCheck }] : []),
    ...(canSuperadmin ? [{ label: t("nav.security"), to: "/admin/security", icon: ShieldAlert }] : []),
    ...(canSuperadmin ? [{ label: t("nav.emailTest"), to: "/admin/email-test", icon: Mail }] : [])
  ];

  const commands = useMemo(() => [
    ...navigation.map((item) => ({ label: t("command.go", { label: item.label }), to: item.to, icon: item.icon })),
    { label: t("command.create"), to: "/schedule?new=request", icon: ClipboardList },
    { label: t("command.profile"), to: "/profile", icon: UserRound }
  ].filter((item) => item.label.toLowerCase().includes(commandSearch.toLowerCase())), [commandSearch, canAdmin, canSuperadmin, t]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandsOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setNavOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${navOpen ? "sidebar-open" : ""}`}>
        <div className="brand">
          <img className="capacity-mark" src="/capacity-mark.svg" alt="" />
          <span><strong>PLI</strong><small>{t("brand.capacity")}</small></span>
        </div>
        <nav aria-label={t("nav.workspace")}>
          <p className="nav-label">{t("nav.workspace")}</p>
          {navigation.slice(0, 2).map((item) => (
            <NavLink key={item.to} to={item.to}>
              <item.icon aria-hidden />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {canAdmin && (
            <>
              <p className="nav-label">{t("nav.administration")}</p>
              {navigation.slice(2).map((item) => (
                <NavLink key={item.to} to={item.to}>
                  <item.icon aria-hidden />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="sidebar-bottom">
          <a href="mailto:techdesk@pli-automotive.com?subject=PLI%20Capacity%20Planning%20Support">
            <HelpCircle aria-hidden />
            <span>{t("nav.support")}</span>
          </a>
          <span className="environment-label">
            <span aria-hidden />
            {t("nav.internal")}
          </span>
        </div>
      </aside>
      {navOpen && <button className="mobile-scrim" onClick={() => setNavOpen(false)} aria-label={t("menu.closeNavigation")} />}

      <div className="shell-main">
        <header className="topbar">
          <button className="mobile-menu" onClick={() => setNavOpen(true)} aria-label={t("menu.openNavigation")}>
            <MenuIcon aria-hidden />
          </button>
          <button className="command-trigger" onClick={() => setCommandsOpen(true)}>
            <Search aria-hidden />
            <span>{t("command.open")}</span>
            <kbd>Ctrl K</kbd>
          </button>
          <button
            className={`environment-switch ${isTest ? "environment-switch-test" : "environment-switch-live"}`}
            onClick={switchEnvironment}
            title={`Switch to ${isTest ? "LIVE" : "TEST"}`}
          >
            <FlaskConical aria-hidden />
            <span>{isTest ? "TEST" : "LIVE"}</span>
            <small>Switch to {isTest ? "LIVE" : "TEST"}</small>
          </button>
          <div className="profile-wrap">
            <button className="profile-trigger" onClick={() => setProfileOpen((value) => !value)} aria-expanded={profileOpen}>
              <Avatar name={data.user.name || data.user.email} size="small" />
              <span>
                <strong>{data.user.name || data.user.email}</strong>
                <small>{roles.includes("superadmin") ? t("role.superadmin") : roles.includes("admin") ? t("role.admin") : roles.includes("owner") ? t("role.owner") : roles.includes("lab_worker") ? t("role.worker") : t("role.requester")}</small>
              </span>
              <Settings aria-hidden />
            </button>
            {profileOpen && (
              <>
                <button className="popover-dismiss" onClick={() => setProfileOpen(false)} aria-label={t("menu.closeProfile")} />
                <div className="profile-menu">
                  <div>
                    <strong>{data.user.name || data.user.email}</strong>
                    <span>{data.user.email}</span>
                  </div>
                  <label className="language-picker"><span>{t("menu.language")}</span><select value={locale} onChange={(event) => setLocale(event.target.value as typeof locale)}>{supportedLocales.map((item) => <option key={item} value={item}>{localeNames[item]}</option>)}</select></label>
                  <Link to="/profile"><UserRound aria-hidden />{t("menu.profile")}</Link>
                  <a href={data.auth.logoutUrl}><LogOut aria-hidden />{t("menu.signout")}</a>
                </div>
              </>
            )}
          </div>
        </header>
        {isTest && (
          <div className="test-system-banner" role="status">
            <FlaskConical aria-hidden />
            <strong>TEST SYSTEM</strong>
            <span>Test data only — no live bookings are created here.</span>
          </div>
        )}

        <main className="page">
          <header className="page-header">
            <div>
              <nav aria-label="Breadcrumb">
                <Link to="/schedule">{t("brand.capacity")}</Link>
                <span>/</span>
                <span>{t(meta.sectionKey)}</span>
              </nav>
              <h1>{t(meta.titleKey)}</h1>
              <p>{t(meta.descriptionKey)}</p>
            </div>
            <div id="page-actions" />
          </header>
          {children}
        </main>
      </div>

      {commandsOpen && (
        <div className="command-layer" role="presentation">
          <button className="command-scrim" onClick={() => setCommandsOpen(false)} aria-label={t("command.close")} />
          <section className="command-palette" role="dialog" aria-modal="true" aria-label={locale === "en" ? "Command palette" : t("command.title")}>
            <div className="command-input">
              <Command aria-hidden />
              <input
                autoFocus
                placeholder={t("command.search")}
                value={commandSearch}
                onChange={(event) => setCommandSearch(event.target.value)}
              />
              <Button iconOnly tone="quiet" aria-label={t("command.close")} onPress={() => setCommandsOpen(false)}><X aria-hidden /></Button>
            </div>
            <div className="command-results">
              <p>{t("command.title")}</p>
              {commands.map((item) => (
                <button
                  key={`${item.to}-${item.label}`}
                  onClick={() => {
                    setCommandsOpen(false);
                    navigate(item.to);
                  }}
                >
                  <item.icon aria-hidden />
                  <span>{item.label}</span>
                  <kbd>Enter</kbd>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
